import React, { useCallback, useContext, useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';
import { useLocation } from 'react-router-dom';

import styled from '@emotion/styled';

import { media } from '../../services/media';

import { Button } from '../Button';
import { Fields } from '../Fields';
import { Loader } from '../Loader';
import { FirebaseFormError } from './FirebaseFormError';
import { Storyblok } from '../../storyblok';
import { useFormFields } from '../../hooks';
import { FirebaseContext, CoreContext, Firebase } from '../../providers';

const emailCodes = [
    'auth/invalid-email',
    'auth/invalid-email-verified',
    'auth/user-not-found',
    'auth/email-already-in-use',
];
const passwordCodes = ['auth/wrong-password', 'auth/weak-password'];

interface FirebaseFormProps {
    fields: Storyblok.Component.InputField[];
    type: Firebase.FormSubmitType;
    onFormSuccess: () => void;
    buttonLabel: string;
    successMessage: string;
}

/** @type {React.FC<FirebaseForm>} */
export const FirebaseForm: React.FC<FirebaseFormProps> = ({
    type,
    onFormSuccess,
    fields,
    buttonLabel,
    successMessage,
}) => {
    const { auth, firebaseFormSubmit } = useContext(FirebaseContext);
    const { settings } = useContext(CoreContext);
    const location = useLocation();
    const [loadingState, setLoadingState] = useState(false);
    const [successSubmitState, setSuccessSubmitState] = useState('');

    const [fieldValues, setFieldValues, resetFieldValues] =
        useFormFields(fields);
    const [fieldErrors, setFieldErrors, resetFieldErrors] =
        useFormFields(fields);

    const [generalError, setGeneralError] = useState('');

    const handleFormError = useCallback(
        (formError) => {
            const { code, message } = formError;

            setGeneralError(message);
            if (emailCodes.includes(code)) {
                setFieldErrors({
                    email: message,
                });
            } else if (passwordCodes.includes(code)) {
                setFieldErrors({
                    password: message,
                });
            } else {
                setFieldErrors({
                    password: message,
                    email: message,
                });
            }
        },
        [setFieldErrors, setGeneralError],
    );

    const handleFormSuccess = useCallback(() => {
        setSuccessSubmitState(successMessage);
        if (onFormSuccess && typeof onFormSuccess === 'function') {
            onFormSuccess();
        }
    }, [successMessage, onFormSuccess]);

    const formSubmitHandler = useCallback(
        (event) => {
            event.preventDefault();

            setLoadingState(true);

            firebaseFormSubmit({
                type,
                data: {
                    ...fieldValues,
                },
            })
                .then(() => {
                    const { googleTagManager } = settings.trackingServices;
                    if (type === 'firebase_register' && googleTagManager) {
                        TagManager.dataLayer({
                            dataLayer: { event: 'Register' },
                        });
                    }
                    handleFormSuccess();
                })
                .catch(handleFormError)
                .finally(() => setLoadingState(false));
        },
        [fieldValues, handleFormSuccess, handleFormError, type],
    );

    useEffect(() => {
        resetFieldErrors();
        resetFieldValues();
        setGeneralError('');
    }, [location.pathname]);

    if (loadingState) {
        return <Loader centered backgroundColor="#ffffff" />;
    }

    if (successSubmitState) {
        return (
            <p className="FirebaseForm__success-message">
                {successSubmitState}
            </p>
        );
    }

    return (
        <Form onSubmit={formSubmitHandler}>
            {generalError && (
                <FirebaseFormError>{generalError}</FirebaseFormError>
            )}

            <Fields
                fields={fields}
                fieldValues={fieldValues}
                setFieldValues={setFieldValues}
                fieldErrors={fieldErrors}
                setFieldErrors={setFieldErrors}
                inputType="formField"
            />

            {buttonLabel && (
                <Button
                    variant="secondary"
                    type="submit"
                    data-event={`firebase form (button ${buttonLabel})`}
                >
                    {buttonLabel}
                </Button>
            )}
        </Form>
    );
};

const Form = styled.form`
    .FormField {
        &__label {
            margin-bottom: 18px;

            font-size: 11px;

            ${media.min('md')} {
                margin-bottom: 24px;

                font-size: 13px;
            }

            ${media.min('xl')} {
                margin-bottom: 1em;

                font-size: 1.7vw;
            }

            ${media.min('xxl')} {
                margin-bottom: 32px;

                font-size: 32px;
            }

            span {
                margin: 0;
            }
        }

        &__input {
            padding: 4px 0;

            margin-bottom: 7px;

            font-size: 15px;

            border-bottom-width: 3px;

            ${media.min('md')} {
                font-size: 16px;
            }

            ${media.min('xl')} {
                border-bottom-width: 5px;
            }

            ${media.min('xxl')} {
                margin-bottom: 0.2em;

                font-size: 40px;

                border-bottom-width: 8px;
            }
        }
    }

    button[type='submit'] {
        display: block;

        min-width: 100px;
        padding: 14px 26px;

        margin: 0 auto;

        outline: none;

        ${media.min('lg')} {
            font-size: 20px;
        }

        ${media.min('xl')} {
            padding: 1.6vw 2.6vw;

            margin-top: 1.58vw;

            font-size: 1.88vw;
        }

        ${media.min('xxl')} {
            padding: 30px 50px;

            margin-top: 30px;
            font-size: 36px;
        }
    }

    input[type='number']::-webkit-inner-spin-button,
    input[type='number']::-webkit-outer-spin-button {
        margin: 0;

        -webkit-appearance: none;
    }
`;
